import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import {
  CreditCard,
  Star,
  CheckCircle,
  Scanner,
  AllInclusive
} from '@mui/icons-material';
import { PROFILE } from '../global/apiKeys';

const PricingCard = () => {
  const [userCredits, setUserCredits] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Pricing plans
  const pricingPlans = [
    {
      id: 'plan_500',
      name: '500 Scans',
      credits: 500,
      price: 500,
      currency: '₹',
      popular: false,
      features: ['500 Card Scans', 'Valid for 1 Year', 'Email Support']
    },
    {
      id: 'plan_1000',
      name: '1000 Scans',
      credits: 1000,
      price: 800,
      currency: '₹',
      popular: true,
      features: ['1000 Card Scans', 'Valid for 1 Year', 'Priority Support', 'Export to PDF']
    },
    {
      id: 'plan_unlimited',
      name: 'Unlimited',
      credits: -1, // -1 means unlimited
      price: 3000,
      currency: '₹',
      popular: false,
      features: ['Unlimited Scans', 'Valid for 1 Year', '24/7 Support', 'Premium Features', 'API Access']
    }
  ];

  // Fetch user credits on component mount
  useEffect(() => {
    fetchUserCredits();

    // Set up periodic refresh every 30 seconds to catch admin changes
    const refreshInterval = setInterval(() => {
      fetchUserCredits();
    }, 30000);

    // Listen for credit refresh events from scan operations
    const handleCreditRefresh = (event) => {
      console.log('Credit refresh event received:', event.detail);
      fetchUserCredits();
    };

    window.addEventListener('refreshCredits', handleCreditRefresh);

    // Cleanup interval and event listener on unmount
    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('refreshCredits', handleCreditRefresh);
    };
  }, []);

  const fetchUserCredits = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('idToken'));
      const response = await fetch(PROFILE, {
        headers: {
          'Authorization': `Bearer ${token?.jwtToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.credits) {
          // Extract credit information from profile data
          const creditData = {
            remainingScans: result.data.credits.remainingScans,
            totalScans: result.data.credits.totalScans,
            planType: result.data.credits.planType,
            expiryDate: result.data.credits.expiryDate
          };

          console.log('Credits fetched from profile API:', creditData);
          setUserCredits(creditData);
        } else {
          console.error('Invalid profile API response structure:', result);
          // Handle API error - set default free credits
          setUserCredits({
            remainingScans: 50,
            totalScans: 50,
            planType: 'free',
            expiryDate: null
          });
        }
      } else {
        console.error('Profile API request failed:', response.status, response.statusText);
        // Set default free credits for new users
        setUserCredits({
          remainingScans: 50,
          totalScans: 50,
          planType: 'free',
          expiryDate: null
        });
      }
    } catch (error) {
      console.error('Error fetching user credits from profile:', error);
      // Set default free credits for new users
      setUserCredits({
        remainingScans: 50,
        totalScans: 50,
        planType: 'free',
        expiryDate: null
      });
    }
  };

  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    // Here you would typically integrate with a payment gateway
    alert(`Selected plan: ${plan.name} for ${plan.currency}${plan.price}\n\nNote: Payment integration would be implemented here.\nFor now, contact admin to manually activate this plan.`);
  };

  const getProgressValue = () => {
    if (!userCredits) return 0;
    if (userCredits.planType === 'unlimited') return 0; // Show empty for unlimited

    const used = userCredits.totalScans - userCredits.remainingScans;
    const total = userCredits.totalScans || 1; // Avoid division by zero
    const percentage = (used / total) * 100;

    // Debug logging
    console.log('Credit calculation:', {
      used,
      total,
      remaining: userCredits.remainingScans,
      percentage: percentage.toFixed(1),
      planType: userCredits.planType,
      calculation: `${used} used out of ${total} total (${userCredits.remainingScans} remaining)`
    });

    return Math.min(Math.max(percentage, 0), 100); // Clamp between 0-100
  };

  const getProgressColor = () => {
    const progress = getProgressValue();
    if (progress >= 90) return 'error';
    if (progress >= 70) return 'warning';
    return 'primary';
  };

  if (!userCredits) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography>Loading credits...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Scanner sx={{ mr: 1 }} />
              <Typography variant="h6">Scan Credits</Typography>
              {userCredits.planType !== 'free' && (
                <Chip 
                  label={userCredits.planType === 'unlimited' ? 'UNLIMITED' : 'PREMIUM'} 
                  size="small" 
                  sx={{ ml: 2, backgroundColor: 'rgba(255,255,255,0.2)' }}
                />
              )}
            </Box>
            <Button
              size="small"
              onClick={fetchUserCredits}
              sx={{ color: 'rgba(255,255,255,0.8)', minWidth: 'auto', p: 0.5 }}
            >
              🔄
            </Button>
          </Box>

          {userCredits.planType === 'unlimited' || userCredits.remainingScans === -1 ? (
            <Box display="flex" alignItems="center" mb={2}>
              <AllInclusive sx={{ mr: 1, color: '#4caf50' }} />
              <Typography variant="h4" sx={{ color: '#4caf50' }}>Unlimited Scans</Typography>
            </Box>
          ) : (
            <Box mb={2}>
              <Typography variant="h4" component="div">
                {userCredits.remainingScans || 0}
                <Typography variant="body2" component="span" sx={{ opacity: 0.8, ml: 1 }}>
                  of {userCredits.totalScans || 0} remaining
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                Used: {(userCredits.totalScans || 0) - (userCredits.remainingScans || 0)} scans
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getProgressValue()}
                color={getProgressColor()}
                sx={{
                  mt: 1,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }}
              />
            </Box>
          )}

          {userCredits.expiryDate && (
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
              Plan expires: {new Date(userCredits.expiryDate).toLocaleDateString()}
            </Typography>
          )}

          {userCredits.remainingScans <= 5 &&
           userCredits.remainingScans > 0 &&
           userCredits.planType !== 'unlimited' &&
           userCredits.remainingScans !== -1 && (
            <Alert
              severity="warning"
              sx={{ mb: 2, backgroundColor: 'rgba(255,255,255,0.9)', color: 'text.primary' }}
            >
              Low on credits! Only {userCredits.remainingScans} scan{userCredits.remainingScans === 1 ? '' : 's'} remaining.
            </Alert>
          )}

          {userCredits.remainingScans === 0 &&
           userCredits.planType !== 'unlimited' &&
           userCredits.remainingScans !== -1 && (
            <Alert
              severity="error"
              sx={{ mb: 2, backgroundColor: 'rgba(255,255,255,0.9)', color: 'text.primary' }}
            >
              No scans remaining! Please upgrade your plan to continue scanning.
            </Alert>
          )}

          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() => setShowPlans(true)}
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}
          >
            {userCredits.planType === 'free' ? 'Upgrade Plan' : 'Change Plan'}
          </Button>
        </CardContent>
      </Card>

      {/* Pricing Plans Dialog */}
      <Dialog open={showPlans} onClose={() => setShowPlans(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div" align="center">
            Choose Your Plan
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            Unlock more scanning power with our premium plans
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2}>
            {pricingPlans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card 
                  sx={{ 
                    position: 'relative',
                    height: '100%',
                    border: plan.popular ? '2px solid #667eea' : '1px solid #e0e0e0',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{ position: 'absolute', top: 16, right: 16 }}
                    />
                  )}
                  
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" component="div" gutterBottom>
                      {plan.name}
                    </Typography>
                    
                    <Box display="flex" alignItems="baseline" mb={2}>
                      <Typography variant="h3" component="div" color="primary">
                        {plan.currency}{plan.price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        /year
                      </Typography>
                    </Box>

                    <Box mb={3}>
                      {plan.features.map((feature, index) => (
                        <Box key={index} display="flex" alignItems="center" mb={1}>
                          <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 16 }} />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      variant={plan.popular ? "contained" : "outlined"}
                      color="primary"
                      fullWidth
                      size="large"
                      onClick={() => handleUpgrade(plan)}
                      startIcon={<CreditCard />}
                    >
                      Choose Plan
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowPlans(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PricingCard;